// Weight management: trend, projected adult size, calorie needs and body
// condition. All estimates — the vet and his body condition come first.

import { ageOn, growthBand } from './age.ts';
import { parseISODate, toISODate } from './time.ts';
import type { LogEvent, Millis, Profile } from './types.ts';

export interface WeightSummary {
  latest?: { kg: number; at: Millis };
  gramsPerWeek?: number;
  band?: { low: number; high: number };
  bcs?: number;
  projectedAdultKg?: { low: number; high: number };
  status?: 'light' | 'on-track' | 'heavy';
}

/** Share of adult weight typically reached by age in weeks (medium breed, rough). */
const ADULT_FRACTION: [number, number][] = [
  [8, 0.22],
  [12, 0.33],
  [16, 0.47],
  [20, 0.6],
  [26, 0.72],
  [39, 0.88],
  [52, 0.96],
  [65, 1],
];

export function adultFraction(weeks: number): number | undefined {
  if (weeks < ADULT_FRACTION[0][0]) return undefined;
  for (let i = 0; i < ADULT_FRACTION.length - 1; i++) {
    const [w0, f0] = ADULT_FRACTION[i];
    const [w1, f1] = ADULT_FRACTION[i + 1];
    if (weeks >= w0 && weeks <= w1) return f0 + ((weeks - w0) / (w1 - w0)) * (f1 - f0);
  }
  return 1;
}

export function weightSummary(events: LogEvent[], profile: Pick<Profile, 'dob'> | undefined, now: Millis): WeightSummary {
  const ws = events.filter((e): e is LogEvent<'weight'> => e.type === 'weight').sort((a, b) => a.at - b.at);
  if (!ws.length) return {};
  const last = ws[ws.length - 1];
  const out: WeightSummary = { latest: { kg: last.data.kg, at: last.at }, bcs: [...ws].reverse().find((w) => w.data.bcs)?.data.bcs };
  // Trend over the last ~3 weeks of weigh-ins (least squares), robust to one-off readings.
  const recent = ws.filter((w) => w.at >= last.at - 21 * 86_400_000);
  if (recent.length >= 2) {
    const xs = recent.map((w) => w.at / (7 * 86_400_000));
    const ys = recent.map((w) => w.data.kg);
    const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
    const my = ys.reduce((a, b) => a + b, 0) / ys.length;
    const num = xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0);
    const den = xs.reduce((a, x) => a + (x - mx) ** 2, 0);
    if (den > 0) out.gramsPerWeek = (num / den) * 1000;
  }
  if (profile?.dob) {
    const weeks = (last.at - parseISODate(profile.dob).getTime()) / (7 * 86_400_000);
    out.band = growthBand(weeks);
    const frac = adultFraction(weeks);
    if (frac && weeks < 52) {
      const est = last.data.kg / frac;
      out.projectedAdultKg = { low: Math.round(est * 0.9 * 2) / 2, high: Math.round(est * 1.1 * 2) / 2 };
    }
    if (out.band) out.status = last.data.kg < out.band.low - 0.4 ? 'light' : last.data.kg > out.band.high + 0.6 ? 'heavy' : 'on-track';
  }
  if (out.bcs !== undefined) out.status = out.bcs <= 3 ? 'light' : out.bcs >= 6 ? 'heavy' : out.status ?? 'on-track';
  void now;
  return out;
}

/** Resting energy requirement, kcal/day. */
export function rer(kg: number): number {
  return 70 * kg ** 0.75;
}

/**
 * Daily energy estimate (kcal). Common vet multipliers: puppies under 4 months
 * ~3× RER, 4–12 months ~2× RER, adults ~1.6× (neutered) or 1.8× (entire);
 * weight loss ~1× RER at the target weight.
 */
export function dailyKcal(kg: number, ageWeeks: number, opts: { neutered?: boolean; lose?: boolean; targetKg?: number } = {}): { kcal: number; factor: number } {
  if (opts.lose) return { kcal: rer(opts.targetKg ?? kg), factor: 1 };
  const factor = ageWeeks < 17 ? 3 : ageWeeks < 52 ? 2 : opts.neutered ? 1.6 : 1.8;
  return { kcal: rer(kg) * factor, factor };
}

export function gramsForKcal(kcal: number, kcalPer100g: number): number {
  return Math.round((kcal / kcalPer100g) * 100);
}

export const BCS_SCALE: { score: number; label: string; detail: string }[] = [
  { score: 1, label: 'Emaciated', detail: 'Ribs, spine and hip bones obvious from a distance; no fat.' },
  { score: 2, label: 'Very thin', detail: 'Ribs, spine and hips easily visible; minimal muscle.' },
  { score: 3, label: 'Thin', detail: 'Ribs easily felt and may be visible; obvious waist and tummy tuck.' },
  { score: 4, label: 'Ideal (lean)', detail: 'Ribs easily felt with a slight fat cover; waist visible from above.' },
  { score: 5, label: 'Ideal', detail: 'Ribs felt without excess fat; waist behind ribs from above; tummy tucked from the side.' },
  { score: 6, label: 'Slightly over', detail: 'Ribs felt with slight excess fat; waist visible but not prominent.' },
  { score: 7, label: 'Overweight', detail: 'Ribs hard to feel under fat; waist barely visible; fat over the base of the tail.' },
  { score: 8, label: 'Obese', detail: 'Ribs only felt with firm pressure; no waist; obvious tummy.' },
  { score: 9, label: 'Severely obese', detail: 'Heavy fat over chest, spine and tail base; distended tummy.' },
];

export function ageWeeksAt(dob: string | undefined, at: Millis): number | undefined {
  return dob ? ageOn(dob, toISODate(at)).weeks : undefined;
}
